/**
 * Backend "not computed" convention (WP14a, backend/app/services/not_computed.py).
 * A serializer that cannot supply a figure sends `null` for the field plus an entry in a sibling
 * `not_computed` map; a real computed 0 stays 0 with no entry.
 */
export interface NotComputedEntry {
  status: 'not_computed' | 'partial';
  reason: string;
  missing_inputs: string[];
}

export type NotComputedMap = Record<string, NotComputedEntry>;

/** Reason text for a field, if the backend flagged it. */
export function notComputedReason(map: NotComputedMap | null | undefined, field: string): string | undefined {
  return map?.[field]?.reason;
}

const NET_CLAIM_FALLBACK_SOURCE = 'adjuster_analyzer_net_claim_fallback';

/**
 * Pure mirror of backend `adjuster_not_computed_fields` / `adjuster_position_status`
 * (backend/app/services/not_computed.py). The attorney/case endpoint passes the stored adjuster
 * analysis through untouched, so the client derives the same verdict.
 *
 * Fires ONLY when `position` is absent/empty (supported + questioned + denied) or
 * `supported_total_source` (top level or in `position`) is the net-claim-fallback marker
 * (questioned + denied only). An unmarked 0 is a real engine zero -> `{}`.
 * An explicit `analysis.not_computed` map, if the server sent one, is honoured and wins.
 */
export function adjusterNotComputed(analysis: unknown): NotComputedMap {
  if (!analysis || typeof analysis !== 'object') return {};
  const a = analysis as Record<string, any>;
  const explicit = a.not_computed;
  if (explicit && typeof explicit === 'object' && Object.keys(explicit).length > 0) {
    return explicit as NotComputedMap;
  }
  const position = a.position;
  const hasPosition = !!position && typeof position === 'object' && Object.keys(position).length > 0;
  const entry = (reason: string): NotComputedEntry => ({ status: 'not_computed', reason, missing_inputs: [] });
  if (!hasPosition) {
    const e = entry('adjuster position not produced');
    return { supported_total: { ...e }, questioned_total: { ...e }, denied_total: { ...e } };
  }
  const source = a.supported_total_source || position.supported_total_source;
  if (source === NET_CLAIM_FALLBACK_SOURCE) {
    const e = entry('no monthly records; line-item assessment not run');
    return { questioned_total: { ...e }, denied_total: { ...e } };
  }
  return {};
}
